import React, { useEffect, useMemo, useState } from "react";
import { BsHourglassSplit, BsPinAngleFill } from "react-icons/bs";
import "../StayUpToDateComponent/StayUpToDate.css";
import "./ShopCardAlignment.css";
import {
  getMediaSrc,
  redirectToUrl,
  openOutboundUrl,
} from "../../Utils/StayUpToDate/RenderComponent";
import ProductName from "../ProductName/ProductName";
import ProductShopButton from "../ProductName/ProductShopButton";
import IndividualYoutubePostTag from "../StayUpToDateComponent/StayUpToDateIndividuelComponent/IndividualYoutubePostTag";
import { NoMediaPost } from "../../Screens/DashboardView/LandingPageCarousels/CommonComponents/NoMediaPost";
import { preserveBrokenImageBox } from "../../Utils/imageUtils";
import { resolveFeedCardImage } from "../../Utils/media/feedCardImage";
import { useFeedMediaPriority } from "../../Utils/media/feedMediaPriority";
import FeedCardPicture from "../media/FeedCardPicture";
import ProductBankImageFrame from "../ProductBank/ProductBankImageFrame";
import { shouldUseProductImageFrame } from "../../Utils/productImage/productImageDisplay";
import {
  stillImageCandidatesFromPost,
  isLikelyVideoUrl,
  hasShoppableProductStill,
} from "../../Utils/media/feedMedia";

const ImagePostTag = React.memo((props: any) => {
  const {
    data,
    brand_color,
    title,
    redirectVariable,
    isLinks,
    isDashboard,
    type,
    isYoutube,
    isHasMediaUrl,
    productName,
    media_url,
    isCarousel,
    contentShape,
    handleRedirect,
    from,
   designTokens,
    pinnedPostIds,
    loading: loadingProp,
    source,
    forceProductFrame = false,
    suppressProductStill = false,
  } = props;
  const redirectUrl: any = redirectToUrl(data, isLinks);
  // Card click must always go to the original post permalink, never to shop_link.
  const permalinkUrl: any = data?.permalink || null;
  const postId = data?.post_id;
  const feedPriority = useFeedMediaPriority();
  const imgLoading = loadingProp === "eager" || feedPriority ? "eager" : "lazy";
  const isPinned = Boolean(
    Array.isArray(pinnedPostIds) &&
    postId != null &&
    pinnedPostIds.includes(postId)
  );
  // Content day-windows: never product-bank frame / product_image still via isLinks.
  const useProductFrame =
    !suppressProductStill &&
    (Boolean(forceProductFrame) || shouldUseProductImageFrame(type, data));

  const openUrlWithTracking = (targetUrl: string | null | undefined) => {
    if (!targetUrl) return;

    handleRedirect(
      data?.post_id,
      getMediaSrc(type, data, isCarousel, media_url),
      data?.platform,
      data?.product_name,
      title,
      isLinks,
      from
    );

    openOutboundUrl(targetUrl);
  };

  const trackShopClick = () => {
    // Shop CTA must always track as shop — never inherit carousel isLinks=false.
    handleRedirect(
      data?.post_id,
      getMediaSrc(type, data, isCarousel, media_url),
      data?.platform,
      data?.product_name,
      title,
      true,
      from
    );
  };

  const renderPinnedBadge = () =>
    isPinned && source !== "edit" ? (
      <div className="pinned-badge" aria-label="Pinned post" style={{right:"4px"}}>
              <BsPinAngleFill />

      </div>
    ) : null;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const targetUrl = data?.permalink;
    if (targetUrl) {
      handleRedirect(
        data?.post_id,
        getMediaSrc(type, data, isCarousel, media_url),
        data?.platform,
        data?.product_name,
        title,
        isLinks,
        from
      );
      // For Safari mobile, use location.href instead of window.open
      openOutboundUrl(targetUrl);
    }
  };

  const platformLc = String(data?.platform || "").toLowerCase();
  const isYoutubePost =
    platformLc === "youtube" ||
    /(?:youtube\.com|youtu\.be)\//i.test(String(data?.permalink || ""));
  // Link in Bio YouTube is the canva card (title + 16:9 thumb + date), not an IG cover tile.
  const useYoutubeCanvaCard = isYoutubePost && !data?.product_image;

  // Prefer a still image for YouTube cover tiles — never paint an .mp4 as <img>.
  // Hooks must run before any early return (rules-of-hooks).
  const youtubeCoverSrc = (() => {
    if (!isYoutubePost) return "";
    const media = String(data?.media_url || media_url || "").trim();
    const thumb = String(data?.thumbnail || "").trim();
    const looksLikeVideoFile =
      /\.(mp4|mov|webm|m3u8|m4v)(\?|#|$)/i.test(media) ||
      /\/video\//i.test(media);
    return looksLikeVideoFile ? thumb || media : media || thumb;
  })();

  const stillCandidates = useMemo(() => {
    const preferProduct =
      useProductFrame ||
      (!suppressProductStill && hasShoppableProductStill(data, isLinks));
    const ranked = stillImageCandidatesFromPost(
      {
        ...data,
        media_url: data?.media_url || media_url,
      },
      { preferProductImage: preferProduct },
    );
    const extras = [youtubeCoverSrc, media_url].filter(
      (u) => u && !isLikelyVideoUrl(String(u)),
    ) as string[];
    const seen = new Set(ranked);
    const out = [...ranked];
    for (const extra of extras) {
      if (extra && !seen.has(extra)) {
        seen.add(extra);
        out.push(extra);
      }
    }
    return out;
  }, [
    data,
    media_url,
    youtubeCoverSrc,
    useProductFrame,
    isLinks,
    suppressProductStill,
  ]);

  const stillCandidatesKey = stillCandidates.join("\0");
  const [srcIndex, setSrcIndex] = useState(0);
  useEffect(() => {
    setSrcIndex(0);
  }, [stillCandidatesKey]);

  const resolvedMediaSrc = stillCandidates[srcIndex] || "";

  if (useYoutubeCanvaCard) {
    return (
      <IndividualYoutubePostTag
        link={data}
        handleRedirect={handleRedirect}
        bgColor={brand_color}
        contentShape={contentShape}
        handleClick={handleClick}
        redirectUrl={data?.permalink || undefined}
        source={source}
        pinnedPostIds={pinnedPostIds}
        isDashboard={isDashboard}
        from={from}
        designTokens={designTokens}
      />
    );
  }

  // On template LiB, never use youtube-feed-* classes — they letterbox vs IG tiles.
  const useYoutubeFeedClasses =
    Boolean(isYoutube) &&
    from !== "landing-page" &&
    from !== "website-template";
  const platformKey = String(data?.platform || "").toLowerCase();
  const isTextPlatform =
    platformKey === "newsletter" ||
    platformKey === "linkedin" ||
    platformKey === "twitter" ||
    platformKey === "x";

  const getWrapperClassNameForShell = () => {
    const baseClass = "creator-feed-image-wrapper-links";
    const classes = [baseClass];
    if (contentShape == "sq" || contentShape === true) classes.push("square-wrapper");
    else if (useYoutubeFeedClasses) classes.push("youtube-feed-wrapper");
    return classes.join(" ");
  };

  const getImageClassNameForShell = () => {
    const baseClass = "creator-feed-card-links-image";
    const classes = [baseClass];
    if (contentShape == "sq" || contentShape === true) classes.push("square-image");
    else if (useYoutubeFeedClasses) classes.push("youtube-feed-height");
    return classes.join(" ");
  };

  const handleStillError = (event: { currentTarget: HTMLImageElement }) => {
    if (srcIndex + 1 < stillCandidates.length) {
      setSrcIndex((i) => i + 1);
      return;
    }
    preserveBrokenImageBox(event);
  };

  const renderPostImageForShell = (src: string) => {
    const imageSrc = resolveFeedCardImage(src, imgLoading === "eager").src;
    if (useProductFrame) {
      return (
        <ProductBankImageFrame
          src={imageSrc}
          alt="post"
          fill
          shape={contentShape}
          loading={imgLoading}
          decoding="async"
          onError={handleStillError}
        />
      );
    }
    return (
      <FeedCardPicture
        url={src}
        className={getImageClassNameForShell()}
        priority={imgLoading === "eager"}
        loading={imgLoading}
        onError={handleStillError}
        style={{ marginBottom: "0%" }}
      />
    );
  };

  const renderMediaShell = () => {
    if (!resolvedMediaSrc && isTextPlatform) {
      return (
        <NoMediaPost
          post={data}
          type={type}
          label={
            platformKey === "newsletter"
              ? data?.caption || "Newsletter"
              : platformKey === "linkedin"
              ? "LinkedIn Post"
              : platformKey === "twitter" || platformKey === "x"
              ? "X Post"
              : "Post"
          }
          className={`newsletter-media-carousel ${getImageClassNameForShell()} no-media-url`}
          showDate={!!data?.post_date}
          showMeta={false}
          isPinned={isPinned}
          designTokens={designTokens}
        />
      );
    }

    if (!resolvedMediaSrc) {
      return (
        <div className={`creator-feed-media-placeholder ${getImageClassNameForShell()}`}>
          <BsHourglassSplit className="hourglassSplit" />
          <p>Media unavailable</p>
        </div>
      );
    }

    return renderPostImageForShell(resolvedMediaSrc);
  };

  // Media shell + Shop/name as siblings so every card shares one Shop baseline.
  return (
    <div
      className="creator-post-stack"
      style={{ position: "relative" }}
    >
      <div
        className={getWrapperClassNameForShell()}
        onClick={() => openUrlWithTracking(permalinkUrl)}
        style={{
          cursor: "pointer",
          position: "relative",
          textDecoration: "none",
          textAlign: isTextPlatform && !resolvedMediaSrc ? "center" : undefined,
          borderRadius: "1rem",
        }}
      >
        {renderPinnedBadge()}
        {renderMediaShell()}
      </div>

      {!isCarousel && (
        <>
          <ProductName type={type} post={data} designTokens={designTokens} />
          <ProductShopButton
            isLinks={isLinks}
            IsIndividual={false}
            type={type}
            post={data}
            isDashboard={isDashboard}
            onBeforeNavigate={trackShopClick}
            designTokens={designTokens}
          />
        </>
      )}
    </div>
  );
});
export default ImagePostTag;
